// =============================================================================
// Fase 5.5 — Step 7: Riepilogo finale + privacy + submit
// =============================================================================
import React from "react";
import { CheckCircle2, XCircle, FileText, Send, ExternalLink, Loader2 } from "lucide-react";
import { Label } from "../../components/ui/label";
import { Checkbox } from "../../components/ui/checkbox";

const DOC_LABELS = {
  documento_identita_dipendente: "Documento d'identità dipendente",
  badge_dipendente: "Badge aziendale dipendente",
  busta_paga_dipendente: "Busta paga dipendente",
  stato_famiglia: "Stato di famiglia",
  documento_identita_stesso_nucleo_familiare: "Documento d'identità familiare",
  libretto_veicolo_da_assicurare: "Libretto veicolo da assicurare",
  libretto_veicolo_classe_merito: "Libretto veicolo (classe merito)",
  attestato_rischio_disponibile: "Attestato di rischio",
  documento_vendita_rottamazione: "Documento vendita/rottamazione",
  libretto_nuovo_veicolo: "Libretto nuovo veicolo",
  iban_rimborso: "IBAN per rimborso",
};

const TIPO_LABEL = {
  nuova: "Nuova polizza",
  rinnovo: "Rinnovo polizza",
  fermo: "Veicolo fermo / non assicurato da tempo",
  contatto: "Richiesta contatto da esperto",
  cambio_veicolo: "Cambio veicolo / vendita / rottamazione",
};

const SUB_OPERAZIONE_LABEL = {
  cambio: "Cambio veicolo",
  vendita_rottamazione: "Vendita o rottamazione",
};

const CANALE_LABEL = {
  chiamata: "Chiamata telefonica",
  whatsapp: "WhatsApp",
  email: "Email",
};

const FASCE_LABEL = {
  mattina: "Mattina (9-12:30)",
  pausa_pranzo: "Pausa pranzo (12:30-14:00)",
  pomeriggio: "Pomeriggio (14-17)",
  tardo_pomeriggio: "Tardo pomeriggio (17-19:30)",
};

const GARANZIE_LABEL = {
  rca: "RC Auto",
  furto_incendio: "Furto & Incendio",
  kasko: "Kasko / CVT",
  cristalli: "Cristalli",
  infortuni_conducente: "Infortuni conducente",
  assistenza_stradale: "Assistenza stradale",
  tutela_legale: "Tutela legale",
};

export function Step7Riepilogo({
  state, setField, partner,
  docs, requiredDocs, missingRequiredDocs,
  submitting, onSubmit,
  submitError,
}) {
  // Motivi per cui il submit è disabilitato (mostrati esplicitamente all'utente)
  // Ogni blocker ha un `anchor` = testid del campo mancante, per link-scroll cliccabile.
  const submitBlockers = [];
  if (missingRequiredDocs.length > 0 && state.tipo_richiesta !== "contatto") {
    submitBlockers.push({
      msg: `Devi caricare ${missingRequiredDocs.length} documento/i obbligatorio/i (torna indietro allo step Documenti).`,
      anchor: null,  // non c'è ancora nel DOM di questo step
    });
  }
  if (!state.privacy_ok) submitBlockers.push({
    msg: "Devi accettare l'informativa privacy.",
    anchor: "rca-privacy-ok",
  });
  if (!state.trattamento_dati_ok) submitBlockers.push({
    msg: "Devi autorizzare il trattamento dei dati.",
    anchor: "rca-trattamento-ok",
  });
  const canSubmit = submitBlockers.length === 0 && !submitting;

  // Handler link-scroll: scrolla al campo mancante e lo evidenzia con ring animato.
  const scrollToBlocker = (anchor) => {
    if (!anchor) return;
    const el = document.querySelector(`[data-testid="${anchor}"]`);
    if (!el) return;
    // Scrolla al parent label per una vista più chiara
    const target = el.closest("label") || el;
    target.scrollIntoView({ behavior: "smooth", block: "center" });
    target.classList.add("ring-4", "ring-amber-400", "ring-offset-2", "rounded-lg", "transition-all");
    setTimeout(() => {
      target.classList.remove("ring-4", "ring-amber-400", "ring-offset-2");
    }, 2500);
  };

  return (
    <div className="space-y-4" data-testid="rca-step7">
      <div>
        <h2 className="text-lg sm:text-xl font-semibold text-[#0F1F14]">
          Riepilogo della richiesta
        </h2>
        <p className="text-sm text-[#4A5E51] mt-1">
          Controlla i dati prima di inviare. Puoi tornare indietro per modificare.
        </p>
      </div>

      {/* Card riepilogo */}
      <div className="rounded-2xl border border-[#E5E2DB] bg-white p-4 space-y-3">
        <RowItem label="Azienda convenzionata" value={partner?.name || "—"} />
        <RowItem
          label="Soggetto della richiesta"
          value={state.soggetto_richiesta === "dipendente"
            ? "Dipendente convenzionato"
            : "Familiare del dipendente"}
        />
        <RowItem label="Tipo di richiesta" value={TIPO_LABEL[state.tipo_richiesta] || "—"} />
        {state.tipo_richiesta === "cambio_veicolo" && (
          <>
            <RowItem
              label="Operazione"
              value={SUB_OPERAZIONE_LABEL[state.sub_operazione] || "—"}
            />
            <RowItem
              label="Targa veicolo interessato"
              value={state.targa_veicolo_cambiato || "—"}
            />
          </>
        )}
        {state.tipo_richiesta === "nuova" && state.rc_familiare && (
          <RowItem
            label="RC Familiare (Bersani)"
            value={
              state.rc_familiare === "si" ? "Sì (da verificare)"
              : state.rc_familiare === "no" ? "No"
              : "Non lo sa (verifica agenzia)"
            }
          />
        )}
        {state.tipo_richiesta !== "contatto" && state.veicolo?.targa && (
          <RowItem
            label="Veicolo"
            value={`${(state.veicolo.tipo_veicolo || "").toUpperCase()} · ${state.veicolo.targa}`}
          />
        )}
        {state.tipo_richiesta !== "contatto" && Array.isArray(state.garanzie_interesse) && state.garanzie_interesse.length > 0 && (
          <RowItem
            label="Garanzie richieste"
            value={
              <span data-testid="rca-riepilogo-garanzie">
                {state.garanzie_interesse
                  .map((k) => GARANZIE_LABEL[k] || k)
                  .join(", ")}
              </span>
            }
          />
        )}
        <RowItem
          label="Preferenza di contatto"
          value={CANALE_LABEL[state.contatto_preferito] || "—"}
        />
        {state.contatto_preferito === "chiamata" && (
          <RowItem
            label="Giorno e fascia"
            value={`${state.contatto_dettagli.giorno || "—"} · ${FASCE_LABEL[state.contatto_dettagli.fascia_oraria] || "—"}`}
          />
        )}
        <RowItem
          label="Contatto"
          value={
            <span>
              {state.contatto.nome} {state.contatto.cognome}
              {state.contatto.telefono && <> · {state.contatto.telefono}</>}
              {state.contatto.email && <> · {state.contatto.email}</>}
            </span>
          }
        />
      </div>

      {/* Documenti */}
      {state.tipo_richiesta !== "contatto" && (
        <div className="rounded-2xl border border-[#E5E2DB] bg-white p-4 space-y-2">
          <p className="text-sm font-semibold text-[#0F1F14] mb-2">
            <FileText className="inline w-4 h-4 mr-1" />
            Documenti
          </p>
          {[...requiredDocs.required, ...requiredDocs.optional].map((docId) => {
            const d = docs[docId];
            const uploaded = !!(d && d.file_id && d.status !== "non_richiesto");
            const isReq = requiredDocs.required.includes(docId);
            return (
              <div
                key={docId}
                className="flex items-center gap-2 text-[12px] py-1"
                data-testid={`rca-riepilogo-doc-${docId}`}
              >
                {uploaded ? (
                  <CheckCircle2 className="w-4 h-4 text-emerald-600" />
                ) : (
                  <XCircle className={`w-4 h-4 ${isReq ? "text-red-500" : "text-gray-400"}`} />
                )}
                <span className={uploaded ? "text-[#0F1F14]" : isReq ? "text-red-700" : "text-[#7A8E83]"}>
                  {DOC_LABELS[docId] || docId}
                  {!isReq && " (facoltativo)"}
                </span>
              </div>
            );
          })}
        </div>
      )}

      {/* Privacy */}
      <div className="rounded-2xl border border-[#E5E2DB] bg-white p-4 space-y-3">
        <p className="text-sm font-semibold text-[#0F1F14]">Privacy e trattamento dati</p>
        <label className="flex items-start gap-3 cursor-pointer">
          <Checkbox
            checked={state.privacy_ok}
            onCheckedChange={(v) => setField("privacy_ok", !!v)}
            data-testid="rca-privacy-ok"
          />
          <span className="text-[12px] text-[#0F1F14] leading-snug">
            Ho preso visione dell'
            <a
              href="/privacy"
              target="_blank"
              rel="noreferrer"
              className="underline text-[var(--brand-accent,#163321)] inline-flex items-center gap-0.5"
            >
              informativa privacy <ExternalLink className="w-3 h-3" />
            </a>. *
          </span>
        </label>
        <label className="flex items-start gap-3 cursor-pointer">
          <Checkbox
            checked={state.trattamento_dati_ok}
            onCheckedChange={(v) => setField("trattamento_dati_ok", !!v)}
            data-testid="rca-trattamento-ok"
          />
          <span className="text-[12px] text-[#0F1F14] leading-snug">
            Autorizzo il trattamento dei dati inseriti e dei documenti caricati per la
            gestione della mia richiesta di preventivo assicurativo. *
          </span>
        </label>
        <label className="flex items-start gap-3 cursor-pointer">
          <Checkbox
            checked={state.marketing_ok}
            onCheckedChange={(v) => setField("marketing_ok", !!v)}
            data-testid="rca-marketing-ok"
          />
          <span className="text-[12px] text-[#4A5E51] leading-snug">
            (Facoltativo) Acconsento a ricevere comunicazioni commerciali di Colucci Assicuratori.
          </span>
        </label>
        {/* FASE K2 — opt-in creazione account cliente (default false, GDPR opt-in esplicito) */}
        <label
          className="flex items-start gap-3 cursor-pointer p-2.5 rounded-xl border border-slate-200 hover:border-emerald-300 bg-slate-50"
          data-testid="rca-crea-account-label"
        >
          <Checkbox
            checked={state.crea_account_ok}
            onCheckedChange={(v) => setField("crea_account_ok", !!v)}
            data-testid="rca-crea-account-ok"
          />
          <span className="text-[12px] text-[#0F1F14] leading-snug">
            <strong>Voglio creare un account gratuito nell&apos;Area Cliente.</strong>{" "}
            Riceverai email e password temporanea a fine invio, mostrata una
            sola volta. (facoltativo)
          </span>
        </label>
      </div>

      {/* Blocco invio */}
      {missingRequiredDocs.length > 0 && state.tipo_richiesta !== "contatto" && (
        <div
          className="rounded-xl border-l-4 border-red-500 bg-red-50 p-3 text-[12px] text-red-900"
          data-testid="rca-missing-docs-warning"
        >
          Per inviare la richiesta è necessario caricare tutti i documenti obbligatori indicati.
        </div>
      )}

      {/* NUOVO: motivi di blocco visibili in caso di pulsante disabilitato */}
      {submitBlockers.length > 0 && (
        <div
          className="rounded-xl border-l-4 border-amber-500 bg-amber-50 p-3 text-[12px] text-amber-900"
          data-testid="rca-submit-blockers"
        >
          <strong className="block mb-1">
            Il pulsante &quot;Invia richiesta&quot; è disabilitato perché:
          </strong>
          <ul className="list-disc list-inside space-y-0.5">
            {submitBlockers.map((b, i) => (
              <li key={i}>
                {b.anchor ? (
                  <button
                    type="button"
                    onClick={() => scrollToBlocker(b.anchor)}
                    className="underline decoration-dotted underline-offset-2 hover:text-amber-950 text-left"
                    data-testid={`rca-blocker-link-${b.anchor}`}
                  >
                    {b.msg}
                  </button>
                ) : (
                  <span>{b.msg}</span>
                )}
              </li>
            ))}
          </ul>
        </div>
      )}

      {submitError && (
        <div
          className="rounded-xl border-l-4 border-red-500 bg-red-50 p-3 text-[12px] text-red-900"
          data-testid="rca-submit-error"
        >
          {submitError}
        </div>
      )}

      <button
        type="button"
        onClick={onSubmit}
        disabled={!canSubmit}
        data-testid="rca-btn-submit"
        className={`w-full sm:w-auto inline-flex items-center justify-center gap-2 px-6 py-3 rounded-full text-white text-sm font-semibold transition-all ${
          canSubmit
            ? "bg-[var(--brand-accent,#163321)] hover:opacity-90 shadow-md hover:shadow-lg"
            : "bg-gray-300 cursor-not-allowed"
        }`}
      >
        {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
        {submitting ? "Invio in corso…" : "Invia richiesta"}
      </button>
    </div>
  );
}

function RowItem({ label, value }) {
  return (
    <div className="flex flex-col sm:flex-row sm:items-start gap-1 sm:gap-3 text-[12px]">
      <Label className="text-[11px] font-semibold text-[#7A8E83] sm:w-40 flex-shrink-0">
        {label}
      </Label>
      <div className="text-[#0F1F14]">{value}</div>
    </div>
  );
}
